import type { ZohoContact } from '@deltasuite/zoho-client';
import type { Disposition, WriteQueueKind } from '@deltasuite/supabase-client';
import { useEffect, useReducer, useState } from 'react';
import type { DispositionPayload } from '../../api/types';
import { ContactCard, type DisposeInput } from './ContactCard';
import { LeaderboardPopover } from './LeaderboardPopover';
import { QueueIndicator } from './QueueIndicator';
import { RecoveryPanel } from './RecoveryPanel';
import { SprintSetup } from './SprintSetup';
import { SprintSummary } from './SprintSummary';
import { TransitionScreen } from './TransitionScreen';
import {
  POINTS_BY_DISPOSITION,
  type DispositionRecord,
  type RunnerState,
  type SprintConfig,
} from './types';
import { useWriteQueue } from './useWriteQueue';

interface Props {
  contacts: readonly ZohoContact[];
  onExit: () => void;
}

type Action =
  | { kind: 'START'; config: SprintConfig }
  | { kind: 'DIAL' }
  | { kind: 'DISPOSE'; input: DisposeInput; contact: ZohoContact; now: number }
  | { kind: 'CONTINUE' }
  | { kind: 'SKIP_TRANSITION' }
  | { kind: 'RESUME' }
  | { kind: 'END_NOW' };

function dispositionRecord(
  contact: ZohoContact,
  input: DisposeInput,
  now: number,
): DispositionRecord {
  const points = POINTS_BY_DISPOSITION[input.kind];
  const name = [contact.First_Name, contact.Last_Name].filter(Boolean).join(' ');
  const base = {
    contactId: contact.id,
    contactName: name,
    points,
    durationSeconds: input.durationSeconds,
    occurredAt: now,
  };
  switch (input.kind) {
    case 'telesales':
      return { ...base, disposition: 'telesales', subject: input.subject, notes: input.notes };
    case 'courtesy_call':
      return { ...base, disposition: 'courtesy_call', subject: input.subject, notes: input.notes };
    case 'vme':
      return { ...base, disposition: 'vme' };
    case 'no_answer':
      return { ...base, disposition: 'no_answer' };
    case 'database_update':
      return { ...base, disposition: 'database_update', notes: input.notes };
  }
}

function reducer(state: RunnerState, action: Action): RunnerState {
  switch (action.kind) {
    case 'START':
      return {
        ...state,
        view: 'card',
        pointTarget: action.config.pointTarget,
        startingPoints: action.config.startingPoints,
        startedAt: Date.now(),
        contactIndex: 0,
        callPhase: 'pre',
        callStartedAt: null,
      };

    case 'DIAL':
      return { ...state, callPhase: 'live', callStartedAt: Date.now() };

    case 'DISPOSE': {
      const record = dispositionRecord(action.contact, action.input, action.now);
      // Optimistic UI per brief §4 — local state advances immediately. The
      // network post + retry happens off the reducer (see persistDisposition).
      const history = [...state.history, record];
      const nextIndex = state.contactIndex + 1;
      const sprintDone = nextIndex >= state.contacts.length;
      // R4: sprint also ends when the daily points target is hit. The
      // check is against TODAY'S total (startingPoints + sprint history),
      // not just sprint-scoped points — pointTarget is a daily figure.
      // Summary surfaces a "Keep going" button that resumes on the next
      // contact.
      const sprintPoints = history.reduce((s, r) => s + r.points, 0);
      const todayTotal = state.startingPoints + sprintPoints;
      const targetHit = todayTotal >= state.pointTarget;

      // No-answer: skip transition, jump straight to next card (brief §7).
      if (record.disposition === 'no_answer') {
        return sprintDone || targetHit
          ? { ...state, history, view: 'summary' }
          : {
              ...state,
              history,
              contactIndex: nextIndex,
              callPhase: 'pre',
              callStartedAt: null,
            };
      }

      // Telesales / Courtesy / VM&E / Database Update hit the transition
      // screen, unless the sprint just ended.
      return sprintDone || targetHit
        ? { ...state, history, view: 'summary' }
        : {
            ...state,
            history,
            view: 'transition',
            lastQuoteIndex: state.lastQuoteIndex + 1,
          };
    }

    case 'CONTINUE':
      return {
        ...state,
        view: 'card',
        contactIndex: state.contactIndex + 1,
        callPhase: 'pre',
        callStartedAt: null,
      };

    case 'SKIP_TRANSITION':
      return {
        ...state,
        view: 'card',
        contactIndex: state.contactIndex + 1,
        callPhase: 'pre',
        callStartedAt: null,
      };

    case 'RESUME':
      return { ...state, view: 'card' };

    case 'END_NOW':
      return { ...state, view: 'summary' };
  }
}

function initial(contacts: readonly ZohoContact[]): RunnerState {
  return {
    view: 'setup',
    pointTarget: 30,
    startingPoints: 0,
    startedAt: null,
    contactIndex: 0,
    callPhase: 'pre',
    callStartedAt: null,
    contacts,
    history: [],
    lastQuoteIndex: 0,
  };
}

// Maps the local DisposeInput into a persistence payload the worker accepts.
// No-answer never reaches Zoho per brief §4, so zoho_write is null.
// Telesales / Courtesy / VM&E create a call activity.
// Database Update emits a contact_update on the primary (Dropped=true) +
// a drop_siblings list the worker fans out into per-sibling write_queue rows.
function buildPayload(contact: ZohoContact, input: DisposeInput, now: number): DispositionPayload {
  const occurred_at = new Date(now).toISOString();
  const points =
    input.kind === 'telesales'
      ? 3
      : input.kind === 'courtesy_call'
        ? 2
        : input.kind === 'vme'
          ? 1
          : input.kind === 'database_update'
            ? 3
            : 0;

  let zoho_write: DispositionPayload['zoho_write'] = null;
  let drop_siblings: readonly string[] | null = null;

  if (input.kind === 'telesales' || input.kind === 'courtesy_call') {
    zoho_write = {
      kind: 'call_activity' satisfies WriteQueueKind,
      payload: {
        Subject: input.subject,
        Description: input.notes,
        Call_Purpose: input.kind === 'telesales' ? 'Telesales' : 'Courtesy Call',
        Call_Type: 'Outbound',
        Call_Duration: secondsToMmSs(input.durationSeconds),
        Call_Start_Time: occurred_at,
        Who_Id: contact.id,
      },
    };
  } else if (input.kind === 'vme') {
    zoho_write = {
      kind: 'call_activity',
      payload: {
        Subject: 'VM&E follow-up',
        Call_Purpose: 'Voice Message & Email Combo',
        Call_Type: 'Outbound',
        Call_Duration: secondsToMmSs(input.durationSeconds),
        Call_Start_Time: occurred_at,
        Who_Id: contact.id,
      },
    };
  } else if (input.kind === 'database_update') {
    zoho_write = {
      kind: 'contact_update',
      payload: { id: contact.id, Dropped: true },
    };
    drop_siblings = input.dropSiblings;
  }

  const dispositionEnum: Disposition = input.kind;
  const subject = 'subject' in input ? (input.subject ?? null) : null;
  const notes = 'notes' in input ? (input.notes ?? null) : null;

  return {
    zoho_contact_id: contact.id,
    zoho_company_id: contact.Account_Name?.id ?? null,
    disposition: dispositionEnum,
    points,
    subject,
    notes,
    duration_seconds: input.durationSeconds,
    occurred_at,
    zoho_write,
    drop_siblings,
  };
}

function secondsToMmSs(s: number): string {
  const m = Math.floor(s / 60);
  const ss = (s % 60).toString().padStart(2, '0');
  return `${m}:${ss}`;
}

// R5 leaderboard pop trigger constants.
const POP_MIN_PROGRESS = 0.2;
const POP_MAX_PROGRESS = 0.4;
const POP_GAP_MS = 8 * 60 * 1000;
const POP_MAX_PER_SPRINT = 3;

export function SprintRunner({ contacts, onExit }: Props) {
  const [state, dispatch] = useReducer(reducer, contacts, initial);
  const [showRecovery, setShowRecovery] = useState(false);
  const [showSyncGate, setShowSyncGate] = useState(false);
  const queue = useWriteQueue(state.view !== 'setup' && state.view !== 'summary');

  // R5 leaderboard pop state. Lives outside the reducer so the trigger
  // logic (random-with-deterministic-seed) doesn't pollute the pure
  // state machine.
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [popsShown, setPopsShown] = useState(0);
  const [lastPopAt, setLastPopAt] = useState<number | null>(null);

  // "Today's total" — what the rep cares about. R4: pointTarget is a
  // daily figure, so the runner displays daily progress, not sprint-only.
  const sprintPoints = state.history.reduce((s, r) => s + r.points, 0);
  const pointsScored = state.startingPoints + sprintPoints;

  // R5 mid-call guard. Pop only renders when the rep is between calls:
  // either on the post-disposition transition screen, or on a fresh
  // contact card before the Dial click. Never mid-call.
  const canShowPop =
    state.view === 'transition' || (state.view === 'card' && state.callPhase === 'pre');

  // Trigger evaluation. Fires only on entries into a "safe" state. Never
  // retries within the same call cycle — next chance is the next safe
  // state transition (typically the next disposition).
  useEffect(() => {
    if (!canShowPop) return;
    if (showLeaderboard) return;
    if (popsShown >= POP_MAX_PER_SPRINT) return;
    if (lastPopAt && Date.now() - lastPopAt < POP_GAP_MS) return;
    // First-pop band measured against SPRINT progress, not today-total.
    // pointsScored includes startingPoints, so a rep resuming a sprint
    // partway through the day would otherwise trigger immediately on
    // contact #1 mount before any work happened.
    const sprintProgress = state.pointTarget > 0 ? sprintPoints / state.pointTarget : 0;
    if (
      popsShown === 0 &&
      (sprintProgress < POP_MIN_PROGRESS || sprintProgress > POP_MAX_PROGRESS)
    ) {
      return;
    }
    // Per-sprint deterministic randomness: seed from startedAt + popsShown
    // so two reps' sprints don't pop in lockstep. ~50% fire rate within
    // the band keeps it surprising.
    const seed = (state.startedAt ?? 0) + popsShown * 1009;
    const r = Math.abs(Math.sin(seed * 12.9898) * 43758.5453);
    const fire = r - Math.floor(r) < 0.5;
    if (!fire) return;
    setShowLeaderboard(true);
    setPopsShown((n) => n + 1);
    setLastPopAt(Date.now());
    // Re-eval whenever the safe state or the score changes. We DON'T
    // re-eval mid-call because canShowPop becomes false and the early
    // return fires.
  }, [
    canShowPop,
    showLeaderboard,
    popsShown,
    lastPopAt,
    sprintPoints,
    state.pointTarget,
    state.startedAt,
  ]);

  // Auto-dismiss as soon as the rep dials (callPhase flips to 'live').
  // The popover's own 8s timer covers the no-action case.
  useEffect(() => {
    if (state.callPhase === 'live' && showLeaderboard) {
      setShowLeaderboard(false);
    }
  }, [state.callPhase, showLeaderboard]);

  const persistDisposition = (input: DisposeInput, contact: ZohoContact, now: number) => {
    queue.postDisposition(buildPayload(contact, input, now)).catch(() => {
      // Indicator/recovery panel surfaces the failure; don't crash the runner.
    });
  };

  const handleClose = () => {
    if (queue.state.unsynced) {
      setShowSyncGate(true);
      return;
    }
    onExit();
  };

  if (state.view === 'setup') {
    return (
      <SprintSetup
        contactCount={contacts.length}
        onStart={(config) => dispatch({ kind: 'START', config })}
        onCancel={onExit}
      />
    );
  }

  if (state.view === 'summary') {
    return (
      <>
        <SprintSummary
          history={state.history}
          pointsScored={pointsScored}
          pointTarget={state.pointTarget}
          contactsTouched={state.history.length}
          startedAt={state.startedAt ?? Date.now()}
          endedAt={Date.now()}
          targetHit={pointsScored >= state.pointTarget}
          onKeepGoing={
            state.contactIndex + 1 < state.contacts.length
              ? () => dispatch({ kind: 'CONTINUE' })
              : undefined
          }
          onClose={handleClose}
        />
        {showSyncGate ? (
          <SyncGate
            queueState={queue.state}
            onForceClose={() => {
              setShowSyncGate(false);
              onExit();
            }}
            onOpenRecovery={() => {
              setShowSyncGate(false);
              setShowRecovery(true);
            }}
          />
        ) : null}
        {showRecovery ? (
          <RecoveryPanel
            state={queue.state}
            onRetry={() => void queue.retryAllFailed()}
            onClose={() => setShowRecovery(false)}
          />
        ) : null}
      </>
    );
  }

  if (state.view === 'transition') {
    return (
      <>
        <TransitionScreen
          quoteIndex={state.lastQuoteIndex}
          pointsScored={pointsScored}
          pointTarget={state.pointTarget}
          contactsTouched={state.history.length}
          totalContacts={state.contacts.length}
          onContinue={() => dispatch({ kind: 'CONTINUE' })}
          onSkip={() => dispatch({ kind: 'SKIP_TRANSITION' })}
        />
        <QueueOverlay state={queue.state} onOpen={() => setShowRecovery(true)} />
        {showLeaderboard && canShowPop ? (
          <LeaderboardPopover onClose={() => setShowLeaderboard(false)} />
        ) : null}
        {showRecovery ? (
          <RecoveryPanel
            state={queue.state}
            onRetry={() => void queue.retryAllFailed()}
            onClose={() => setShowRecovery(false)}
          />
        ) : null}
      </>
    );
  }

  if (state.view === 'paused') {
    return (
      <main className="flex min-h-full items-center justify-center">
        <div className="max-w-sm text-center">
          <p className="font-sans text-lg text-graphite-700">Sprint paused.</p>
          <button
            type="button"
            onClick={() => dispatch({ kind: 'RESUME' })}
            className="mt-4 rounded-md bg-signal-amber px-4 py-2 font-sans text-sm font-medium text-surface hover:bg-signal-amber-hover"
          >
            Resume
          </button>
        </div>
      </main>
    );
  }

  // view === 'card'
  const contact = state.contacts[state.contactIndex];
  if (!contact) return null;

  return (
    <>
      <ContactCard
        key={contact.id}
        index={state.contactIndex}
        total={state.contacts.length}
        contact={contact}
        callPhase={state.callPhase}
        callStartedAt={state.callStartedAt}
        pointsScored={pointsScored}
        pointTarget={state.pointTarget}
        vmeTemplateLoaded
        onDial={() => dispatch({ kind: 'DIAL' })}
        onDispose={(input) => {
          const now = Date.now();
          persistDisposition(input, contact, now);
          dispatch({ kind: 'DISPOSE', input, contact, now });
        }}
      />
      <QueueOverlay state={queue.state} onOpen={() => setShowRecovery(true)} />
      {showLeaderboard && canShowPop ? (
        <LeaderboardPopover onClose={() => setShowLeaderboard(false)} />
      ) : null}
      {showRecovery ? (
        <RecoveryPanel
          state={queue.state}
          onRetry={() => void queue.retryAllFailed()}
          onClose={() => setShowRecovery(false)}
        />
      ) : null}
    </>
  );
}

function QueueOverlay({
  state,
  onOpen,
}: {
  state: ReturnType<typeof useWriteQueue>['state'];
  onOpen: () => void;
}) {
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-20">
      <div className="pointer-events-auto rounded-md border border-graphite-100 bg-surface px-3 py-1.5 shadow-sm">
        <QueueIndicator state={state} onOpen={onOpen} />
      </div>
    </div>
  );
}

function SyncGate({
  queueState,
  onOpenRecovery,
  onForceClose,
}: {
  queueState: ReturnType<typeof useWriteQueue>['state'];
  onOpenRecovery: () => void;
  onForceClose: () => void;
}) {
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      className="fixed inset-0 z-30 flex items-center justify-center bg-graphite-900/40 px-4"
    >
      <div className="w-full max-w-md rounded-lg border border-graphite-100 bg-surface p-6 shadow-lg">
        <h2 className="font-sans text-lg font-semibold text-graphite-900">Sync first.</h2>
        <p className="mt-1 font-sans text-sm text-graphite-700">
          {queueState.pending + queueState.failedRetry + queueState.failedTerminal} write{' '}
          {queueState.pending + queueState.failedRetry + queueState.failedTerminal === 1
            ? 'is'
            : 'are'}{' '}
          still in flight. Close anyway and they’ll keep retrying in the background.
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onOpenRecovery}
            className="rounded-md border border-graphite-300 px-3 py-2 font-sans text-sm text-graphite-700 hover:bg-surface-sunk"
          >
            Show queue
          </button>
          <button
            type="button"
            onClick={onForceClose}
            className="rounded-md bg-graphite-900 px-3 py-2 font-sans text-sm font-medium text-surface hover:bg-graphite-700"
          >
            Close anyway
          </button>
        </div>
      </div>
    </div>
  );
}
